import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import { Star } from "lucide-react";
import NovariMarkLoader from "@/components/ui/NovariMarkLoader";
import { useLoadingScreenAnalytics } from "@/hooks/useLoadingScreenAnalytics";

// Read dark mode directly from localStorage — this component renders BEFORE
// KidsDarkModeProvider is mounted, so context isn't available yet.
function useStoredDarkMode() {
  try { return localStorage.getItem("novari_kids_dark_mode") === "true"; } catch { return false; }
}
import { useEffect, useState } from "react";

const MESSAGES = [
  "Getting ready for you!",
  "Loading fun stuff...",
  "Almost there!",
  "Grabbing your books...",
  "One moment!",
];

const STARS = [
  { top: "8%",  left: "6%",  size: 28, delay: 0 },
  { top: "12%", left: "88%", size: 22, delay: 0.4 },
  { top: "72%", left: "4%",  size: 20, delay: 0.8 },
  { top: "78%", left: "92%", size: 26, delay: 0.2 },
  { top: "35%", left: "3%",  size: 16, delay: 1.1 },
  { top: "55%", left: "95%", size: 18, delay: 0.6 },
  { top: "20%", left: "50%", size: 14, delay: 1.4 },
];

export default function KidsLoadingScreen() {
  const shouldReduceMotion = useReducedMotion();
  const isDark = useStoredDarkMode();
  const [messageIndex, setMessageIndex] = useState(0);

  useLoadingScreenAnalytics(true, "kids_loading_screen");

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % MESSAGES.length);
    }, 2200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="fixed inset-0 z-celebration flex flex-col items-center justify-center overflow-hidden transition-colors duration-300"
      style={{
        background: isDark
          ? "#131f2e"
          : "#f3f4f6",
      }}
      role="status"
      aria-live="polite"
      aria-label="Loading"
    >
      {/* Floating star decorations */}
      {!shouldReduceMotion &&
        STARS.map((star, i) => (
          <motion.div
            key={i}
            aria-hidden
            className="absolute pointer-events-none select-none text-yellow-400"
            style={{
              top: star.top,
              left: star.left,
              filter: "drop-shadow(0 2px 6px rgba(255,193,7,0.4))",
            }}
            animate={{ y: [0, -10, 0], opacity: [0.7, 1, 0.7] }}
            transition={{
              duration: 3 + i * 0.3,
              repeat: Infinity,
              ease: "easeInOut",
              delay: star.delay,
            }}
          >
            <Star
              style={{ width: star.size, height: star.size }}
              className="fill-yellow-400 text-yellow-400"
            />
          </motion.div>
        ))}

      {/* Logo */}
      <motion.div
        className="mb-8 relative"
        initial={{ opacity: 0, scale: 0.7, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 180, damping: 18, delay: 0.1 }}
      >
        {/* Glowing halo behind logo */}
        {!shouldReduceMotion && (
          <motion.div
            aria-hidden
            className="absolute inset-0 rounded-full"
            style={{
              background: "radial-gradient(circle, rgba(255,183,77,0.35) 0%, transparent 70%)",
              transform: "scale(1.5)",
            }}
            animate={{ scale: [1.4, 1.7, 1.4], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
        )}
        <NovariMarkLoader size={160} className="relative drop-shadow-xl" />
      </motion.div>

      {/* Message card */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 22, delay: 0.25 }}
        className="flex flex-col items-center gap-5 px-8 py-6 mx-4 w-full max-w-[320px] sm:max-w-sm backdrop-blur-sm rounded-[28px]"
        style={{
          background: isDark ? "#1a2840" : "rgba(255,255,255,0.8)",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.9)"}`,
          boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.5)" : "0 8px 32px rgba(0,0,0,0.1)",
        }}
      >
        {/* Cycling message */}
        <div className="h-9 flex items-center justify-center overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.p
              key={messageIndex}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.28 }}
              className="text-base sm:text-lg font-extrabold text-center leading-tight"
              style={{ color: isDark ? "#f9fafb" : "#0f4c81" }}
            >
              {MESSAGES[messageIndex]}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Bouncing dots */}
        <div className="flex items-center gap-2.5" aria-hidden>
          {[0, 1, 2, 3].map((i) => (
            <motion.div
              key={i}
              className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full"
              style={{
                background: ["#FFB347", "#FF6B9D", "#A78BFA", "#34D399"][i],
              }}
              animate={
                shouldReduceMotion
                  ? {}
                  : { y: [0, -12, 0], scale: [1, 1.15, 1] }
              }
              transition={{
                duration: 0.7,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.14,
              }}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
}
